/**
 * ReferralProgram — investor-portal-scoped referral form (PR #4).
 *
 * Submits to authenticated POST /investor/referrals. Backend creates a CRM
 * lead with landing_source="investor_referral" and referrer attribution.
 * If the referred contact is already in the CRM (30-day dedup), backend
 * records the attempt and returns deduped:true; UX is identical from the
 * investor's perspective ("we received it, team will review").
 *
 * Strings come from useInvestorLanguage (Spanish default, English opt-in
 * via Mi cuenta). 2% wording is intentionally guarded with "sujeta a las
 * condiciones del programa".
 */
import React, { useState } from "react";
import { Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";
import { Share, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const TERRACOTTA = "#9c5236";
const TERRACOTTA_BG = "#fbf3ee";
const TERRACOTTA_BORDER = "#e4cdbf";

const EMPTY = { name: "", email: "", phone: "", message: "" };

export default function ReferralProgram() {
  const { t } = useInvestorLanguage();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!name || (!email && !phone)) {
      setErr(t("referral.error_required"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/investor/referrals", {
        name,
        email: email || null,
        phone: phone || null,
        message: form.message.trim() || null,
      });
      setSuccess(true);
      setForm(EMPTY);
    } catch (e2) {
      setErr(formatError(e2) || t("referral.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={t("referral.title")} subtitle={t("referral.subtitle")}>
      {/* Program intro card */}
      <div
        className="rounded-lg p-5 sm:p-6 border mb-5 bg-[#fbf3ee] dark:bg-stone-900 border-[#e4cdbf] dark:border-stone-700"
        data-testid="referral-intro"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center" style={{ background: TERRACOTTA }}>
            <Share size={18} weight="regular" color="white" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-stone-700 dark:text-stone-200 leading-relaxed">
              {t("referral.intro")}
            </div>
            <div className="text-xs text-stone-500 dark:text-stone-400 mt-2 italic">
              {t("referral.intro_note")}
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      {success ? (
        <div
          className="dv-card flex items-start gap-3"
          data-testid="referral-success"
        >
          <CheckCircle size={20} weight="fill" className="shrink-0 mt-0.5" style={{ color: TERRACOTTA }} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-stone-800 dark:text-stone-100 mb-1">{t("referral.success")}</div>
            <button
              type="button"
              onClick={() => { setSuccess(false); setErr(""); }}
              className="text-sm hover:underline"
              style={{ color: TERRACOTTA }}
              data-testid="referral-send-another"
            >
              {t("referral.send_another")}
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={submit}
          className="dv-card space-y-3 max-w-2xl"
          data-testid="referral-form"
          noValidate
        >
          <h3 className="font-medium text-stone-800 dark:text-stone-100 mb-1">{t("referral.form.title")}</h3>
          <div>
            <label className="text-sm text-stone-600 dark:text-stone-300">{t("referral.field.name")} *</label>
            <input
              type="text"
              required
              maxLength={120}
              className="dv-input mt-1"
              value={form.name}
              onChange={set("name")}
              data-testid="referral-name"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-stone-600 dark:text-stone-300">{t("referral.field.email")}</label>
              <input
                type="email"
                maxLength={200}
                className="dv-input mt-1"
                value={form.email}
                onChange={set("email")}
                data-testid="referral-email"
              />
            </div>
            <div>
              <label className="text-sm text-stone-600 dark:text-stone-300">{t("referral.field.phone")}</label>
              <input
                type="tel"
                maxLength={40}
                className="dv-input mt-1"
                value={form.phone}
                onChange={set("phone")}
                data-testid="referral-phone"
              />
            </div>
          </div>
          <div className="text-xs text-stone-500 dark:text-stone-400">{t("referral.required_hint")}</div>
          <div>
            <label className="text-sm text-stone-600 dark:text-stone-300">{t("referral.field.message")}</label>
            <textarea
              rows={3}
              maxLength={2000}
              className="dv-input mt-1"
              value={form.message}
              onChange={set("message")}
              placeholder={t("referral.field.helper")}
              data-testid="referral-message"
            />
          </div>
          {err && (
            <div className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800/60 rounded px-3 py-2" data-testid="referral-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="dv-btn dv-btn-primary"
            data-testid="referral-submit"
          >
            <PaperPlaneTilt size={14} weight="bold" /> {busy ? t("referral.submitting") : t("referral.submit")}
          </button>
        </form>
      )}
    </Section>
  );
}
